import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  RefreshCw, 
  History, 
  CheckCircle, 
  AlertCircle, 
  XCircle, 
  HelpCircle,
  Clock,
  Activity,
  Loader2
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { API_URL } from '../config/api';
import { useToast } from '../components/ui/use-toast';
import { AgentMetricsCard } from '../components/AgentMetricsCard';
import { ExternalLink } from 'lucide-react';

interface Agent {
  id: number;
  agent_name: string;
  agent_type: string;
  description: string;
  container_name: string;
  is_active: boolean;
  health_status: 'healthy' | 'warning' | 'error' | 'offline' | 'unknown';
  last_heartbeat: string | null;
  consecutive_failures: number;
  last_run_start: string | null;
  last_run_end: string | null;
  last_run_status: string | null;
  last_run_items_processed: number | null;
  last_run_items_succeeded: number | null;
  last_run_error: string | null;
}

interface AgentExecution {
  id: number;
  agent_name: string;
  execution_start: string;
  execution_end: string | null;
  status: string;
  items_processed: number;
  items_succeeded: number;
  items_failed: number;
  error_message: string | null;
  execution_metadata: any;
}

interface AgentStats {
  agent_name: string;
  total_executions: number;
  successful_executions: number;
  failed_executions: number;
  avg_duration_seconds: number | null;
  total_items_processed: number;
  total_items_succeeded: number;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'healthy':
    case 'success':
      return <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400" />;
    case 'warning':
      return <AlertCircle className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />;
    case 'error':
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-500 dark:text-red-400" />;
    case 'offline':
    case 'timeout':
      return <HelpCircle className="h-4 w-4 text-muted-foreground" />;
    default:
      return <HelpCircle className="h-4 w-4 text-muted-foreground" />;
  }
};

const getStatusBadge = (status: string) => {
  const variantMap: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    healthy: 'default',
    success: 'default',
    warning: 'secondary',
    error: 'destructive',
    failed: 'destructive',
    offline: 'outline',
    unknown: 'outline',
    running: 'secondary',
    timeout: 'destructive'
  };

  return (
    <Badge variant={variantMap[status] || 'outline'} className="flex items-center gap-1">
      {getStatusIcon(status)}
      <span>{status}</span>
    </Badge>
  );
};

const formatDuration = (seconds: number | null) => {
  if (!seconds) return 'N/A';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

export default function AgentMonitoring() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [stats, setStats] = useState<AgentStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [executionHistory, setExecutionHistory] = useState<AgentExecution[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { toast } = useToast();

  const fetchAgents = async () => {
    try {
      setRefreshing(true);
      const response = await fetch(`${API_URL}/agent-monitoring/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch agent status');
      }
      
      const data = await response.json();
      setAgents(data.agents);
      setError(null);
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch agents');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/agent-monitoring/stats?days=7`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch agent statistics');
      }
      
      const data = await response.json();
      setStats(data.statistics);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchHistory = async (agentId: number) => {
    try {
      setHistoryLoading(true);
      const response = await fetch(`${API_URL}/agent-monitoring/${agentId}/history?limit=50`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch agent history');
      }
      
      const data = await response.json();
      setExecutionHistory(data.history);
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchStats();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchAgents();
      fetchStats();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const handleViewHistory = (agent: Agent) => {
    setSelectedAgent(agent);
    setHistoryOpen(true);
    fetchHistory(agent.id);
  };

  const handleCloseHistory = () => {
    setHistoryOpen(false);
    setSelectedAgent(null);
    setExecutionHistory([]);
  };

  const renderOverview = () => {
    const activeAgents = agents.filter(a => a.is_active);
    const healthyAgents = agents.filter(a => a.health_status === 'healthy');
    const warningAgents = agents.filter(a => a.health_status === 'warning');
    const errorAgents = agents.filter(a => a.health_status === 'error');

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Agents</CardDescription>
            <CardTitle className="text-3xl">{agents.length}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{activeAgents.length} active</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Healthy</CardDescription>
            <CardTitle className="text-3xl text-green-600 dark:text-green-400">{healthyAgents.length}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {agents.length > 0 ? Math.round((healthyAgents.length / agents.length) * 100) : 0}% of total
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Warnings</CardDescription>
            <CardTitle className="text-3xl text-yellow-600 dark:text-yellow-400">{warningAgents.length}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Need attention</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Errors</CardDescription>
            <CardTitle className="text-3xl text-red-600 dark:text-red-400">{errorAgents.length}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Require action</p>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderAgentTable = () => (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agent Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Heartbeat</TableHead>
              <TableHead>Last Run</TableHead>
              <TableHead>Last Result</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agents.map((agent) => (
              <TableRow key={agent.id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{agent.agent_name}</p>
                    <p className="text-sm text-muted-foreground">{agent.description}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{agent.agent_type}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(agent.health_status)}
                    {agent.consecutive_failures > 0 && (
                      <span className="text-sm text-red-500 dark:text-red-400">
                        ({agent.consecutive_failures} failures)
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {agent.last_heartbeat ? (
                    <div title={format(new Date(agent.last_heartbeat), 'PPpp')}>
                      {formatDistanceToNow(new Date(agent.last_heartbeat), { addSuffix: true })}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Never</span>
                  )}
                </TableCell>
                <TableCell>
                  {agent.last_run_start ? (
                    <div>
                      <p>{formatDistanceToNow(new Date(agent.last_run_start), { addSuffix: true })}</p>
                      {agent.last_run_end && (
                        <p className="text-sm text-muted-foreground">
                          Duration: {formatDuration(
                            (new Date(agent.last_run_end).getTime() - new Date(agent.last_run_start).getTime()) / 1000
                          )}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Never run</span>
                  )}
                </TableCell>
                <TableCell>
                  {agent.last_run_status ? (
                    <div>
                      {getStatusBadge(agent.last_run_status)}
                      {agent.last_run_items_processed !== null && (
                        <p className="text-sm mt-1">
                          {agent.last_run_items_succeeded}/{agent.last_run_items_processed} processed
                        </p>
                      )}
                      {agent.last_run_error && (
                        <p className="text-sm text-red-500 dark:text-red-400 mt-1" title={agent.last_run_error}>
                          View error
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">No results</span>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleViewHistory(agent)}
                  >
                    <History className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  const renderStatistics = () => (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agent Name</TableHead>
              <TableHead className="text-right">Total Runs</TableHead>
              <TableHead className="text-right">Success Rate</TableHead>
              <TableHead className="text-right">Avg Duration</TableHead>
              <TableHead className="text-right">Items Processed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stats.map((stat) => (
              <TableRow key={stat.agent_name}>
                <TableCell>{stat.agent_name}</TableCell>
                <TableCell className="text-right">{stat.total_executions}</TableCell>
                <TableCell className="text-right">
                  {stat.total_executions > 0 ? (
                    <div className="flex items-center justify-end gap-2">
                      <Progress 
                        value={(stat.successful_executions / stat.total_executions) * 100} 
                        className="w-16 h-2"
                      />
                      <span>
                        {Math.round((stat.successful_executions / stat.total_executions) * 100)}%
                      </span>
                    </div>
                  ) : (
                    'N/A'
                  )}
                </TableCell>
                <TableCell className="text-right">{formatDuration(stat.avg_duration_seconds)}</TableCell>
                <TableCell className="text-right">
                  {stat.total_items_processed > 0 ? (
                    <div>
                      <p>{stat.total_items_processed}</p>
                      <p className="text-sm text-muted-foreground">
                        {Math.round((stat.total_items_succeeded / stat.total_items_processed) * 100)}% success
                      </p>
                    </div>
                  ) : (
                    'N/A'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Agent Monitoring</h1>
          <p className="text-muted-foreground">Monitor background agent execution status</p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            fetchAgents();
            fetchStats();
          }}
          disabled={refreshing}
        >
          {refreshing ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="status">Agent Status</TabsTrigger>
          <TabsTrigger value="statistics">Statistics (7 days)</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {renderOverview()}
          <div>
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            {renderAgentTable()}
          </div>
          
          {/* Real-time Metrics Section */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Real-time Agent Metrics</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('http://localhost:3009/d/agent-observability/agent-observability', '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View in Grafana
              </Button>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {agents.map((agent) => (
                <div key={agent.id} className="space-y-2">
                  <h4 className="text-sm font-semibold">{agent.agent_name}</h4>
                  <AgentMetricsCard agentName={agent.agent_name} compact={false} />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="status">
          {renderAgentTable()}
        </TabsContent>

        <TabsContent value="statistics">
          {renderStatistics()}
        </TabsContent>
      </Tabs>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Execution History - {selectedAgent?.agent_name}</DialogTitle>
            <DialogDescription>
              Recent execution history for this agent
            </DialogDescription>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Start Time</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {executionHistory.map((execution) => (
                  <TableRow key={execution.id}>
                    <TableCell>
                      {format(new Date(execution.execution_start), 'PPpp')}
                    </TableCell>
                    <TableCell>
                      {execution.execution_end
                        ? formatDuration(
                            (new Date(execution.execution_end).getTime() -
                              new Date(execution.execution_start).getTime()) /
                              1000
                          )
                        : 'Running...'}
                    </TableCell>
                    <TableCell>{getStatusBadge(execution.status)}</TableCell>
                    <TableCell>
                      {execution.items_processed > 0 && (
                        <>
                          {execution.items_succeeded}/{execution.items_processed}
                          {execution.items_failed > 0 && (
                            <p className="text-sm text-red-500">
                              {execution.items_failed} failed
                            </p>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      {execution.error_message && (
                        <p className="text-sm text-red-500" title={execution.error_message}>
                          {execution.error_message.length > 50
                            ? execution.error_message.substring(0, 50) + '...'
                            : execution.error_message}
                        </p>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}